import type {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  GridOptions,
} from 'ag-grid-community';
import React, { ComponentType, FC, useCallback, useState } from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import styled from 'styled-components';

const Mask = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--zindex-layer-02);
`;

const skipEditingMask = (event: CellEditingStartedEvent) => {
  if (
    event.colDef?.field === 'status' &&
    event.data?.taskId === 'provisional'
  ) {
    return true;
  }
  if (
    event.colDef?.field === 'template-phase-task-title' &&
    ['PROJECT_PHASE', 'TASK'].includes(event.data?.type)
  ) {
    return true;
  }
  if (
    ['reportsTo', 'managerOf'].includes(event.colDef?.field ?? '') &&
    event.data?.type === 'USER'
  ) {
    return true;
  }

  return false;
};

export const withEditingMask = <
  T extends Pick<GridOptions, 'onCellEditingStarted' | 'onCellEditingStopped'>,
>(
  Component: ComponentType<T>,
): FC<T> => {
  const Next: FC<T> = ({
    onCellEditingStarted: started,
    onCellEditingStopped: stopped,
    ...props
  }) => {
    const [editing, setEditing] = useState(false);

    const onCellEditingStarted = useCallback(
      (event: CellEditingStartedEvent) => {
        if (skipEditingMask(event)) {
          return;
        }
        started?.(event);
        setEditing(true);
      },
      [started],
    );

    const onCellEditingStopped = useCallback(
      (event: CellEditingStoppedEvent) => {
        stopped?.(event);
        setEditing(false);
      },
      [stopped],
    );

    return (
      <>
        <Component
          {...(props as any)}
          onCellEditingStarted={onCellEditingStarted}
          onCellEditingStopped={onCellEditingStopped}
        />
        {editing ? <Mask /> : null}
      </>
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithEditingMask'))(Next);
};
